/**
 * {tenant}.inflomatrix.app — Public / Relationship / Business path planes (R1 Phase 1)
 */

import React, { Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { AppLayout } from '@frontend/components/common/AppLayout';
import { MainRouter } from '@frontend/router';
import { useTenant } from '@frontend/contexts/TenantContext';
import {
  buildPlatformUrl,
  getTenantIdFromHost,
  isPlatformOnlyPath,
  matchPathPlane,
} from '@shared/tenant-host';
import { BusinessPlaneGuard } from '../site/frontend/guards/BusinessPlaneGuard';
import { BusinessPlaneProvisioningGuard } from '../site/frontend/guards/BusinessPlaneProvisioningGuard';
import { BusinessBrandThemeProvider } from '../business/frontend/providers/BusinessBrandThemeProvider';
import { TenantNotFoundPage } from './TenantNotFoundPage';
import { ViewingContextBadge } from '../business/frontend/components/common/ViewingContextBadge';
import { Spin } from 'antd';

const SiteRouter = React.lazy(() =>
  import('../site/frontend/router/SiteRouter').then((mod) => ({ default: mod.SiteRouter }))
);

const RelationshipRouter = React.lazy(() =>
  import('../relationship/frontend/router/RelationshipRouter').then((mod) => ({
    default: mod.RelationshipRouter,
  }))
);

export interface TenantHostRouterProps {
  tenantRootDomain: string | null;
}

const TenantHostLoading: React.FC = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
    }}
  >
    <Spin size="large" />
  </div>
);

const TenantHostRouterContent: React.FC<TenantHostRouterProps> = ({ tenantRootDomain }) => {
  const { currentTenant, loading, error } = useTenant();
  const location = useLocation();
  const pathname = location.pathname;
  const plane = matchPathPlane(pathname, 'tenant');

  if (loading) {
    return <TenantHostLoading />;
  }

  if (!currentTenant) {
    const tenantId =
      getTenantIdFromHost(window.location.hostname, tenantRootDomain ?? undefined) ??
      window.location.hostname.split('.')[0] ??
      'unknown';
    if (error === 'TENANT_NOT_FOUND') {
      return <TenantNotFoundPage tenantId={tenantId} tenantRootDomain={tenantRootDomain} />;
    }
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 'var(--space-4)',
          textAlign: 'center',
          padding: 'var(--space-8)',
        }}
      >
        <p style={{ color: 'var(--color-text-secondary)' }}>
          조직 정보를 불러오는 중 오류가 발생했습니다.
        </p>
        <button onClick={() => window.location.reload()}>다시 시도</button>
      </div>
    );
  }

  if (isPlatformOnlyPath(pathname) && tenantRootDomain) {
    const platformUrl = buildPlatformUrl(tenantRootDomain, pathname + location.search);
    window.location.replace(platformUrl);
    return <TenantHostLoading />;
  }

  if (plane === 'public') {
    return (
      <>
        <ViewingContextBadge />
        <Suspense fallback={<TenantHostLoading />}>
          <SiteRouter tenantId={currentTenant.id} />
        </Suspense>
      </>
    );
  }

  if (plane === 'relationship') {
    return (
      <>
        <ViewingContextBadge />
        <Suspense fallback={<TenantHostLoading />}>
          <RelationshipRouter tenantId={currentTenant.id} />
        </Suspense>
      </>
    );
  }

  return (
    <BusinessBrandThemeProvider tenantId={currentTenant.id}>
      <BusinessPlaneGuard>
        <BusinessPlaneProvisioningGuard tenantRootDomain={tenantRootDomain}>
          <ViewingContextBadge />
          <AppLayout>
            <MainRouter tenantId={currentTenant.id} />
          </AppLayout>
        </BusinessPlaneProvisioningGuard>
      </BusinessPlaneGuard>
    </BusinessBrandThemeProvider>
  );
};

export const TenantHostRouter: React.FC<TenantHostRouterProps> = (props) => (
  <TenantHostRouterContent {...props} />
);

export default TenantHostRouter;
